import React, { useState, useMemo } from 'react';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { FeedbackEmpty } from '@components/Feedback/FeedbackEmpty';
import { FeedbackError } from '@components/Feedback/FeedbackError';
import { FeedbackStatus } from '@components/Feedback/FeedbackStatus';
import { Skeleton, SkeletonList } from '@components/Skeleton/Skeleton';
import { Button } from '@components/Button/Button';
import { TableDataProps } from '@/types/components';
import { SortDirection, Column } from '@/types/common';

export const TableData = <T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  sorting,
  selection,
  rowActions,
  emptyState,
  emptyTitle,
  emptyDescription,
  emptyAction,
  onResetFilters,
  error,
  onRetry,
  showHeaderWhenEmpty = false,
  className = '',
  size = 'medium',
  onRowClick,
  mobileRow,
  fullWidthRow,
}: TableDataProps<T>) => {
  const { isMobile } = useBreakpoint();
  const useMobileRows = isMobile && typeof mobileRow === 'function';
  const totalColumns =
    columns.length + (selection ? 1 : 0) + (rowActions ? 1 : 0);

  const [internalSorting, setInternalSorting] = useState<{
    field: string;
    direction: SortDirection;
  }>({
    field: sorting?.field || '',
    direction: sorting?.direction || null,
  });

  // Size variants
  const sizeClasses = {
    small: 'text-xs',
    medium: 'text-[14px]',
    large: 'text-base',
  };

  const cellPadding = {
    small: 'px-3 py-2',
    medium: 'px-6 py-4',
    large: 'px-8 py-6',
  };

  // Handle sorting
  const handleSort = (field: string) => {
    const column = columns.find((col) => col.key === field);
    if (!column?.sortable) return;

    let newDirection: SortDirection = 'asc';

    if (internalSorting.field === field) {
      if (internalSorting.direction === 'asc') {
        newDirection = 'desc';
      } else if (internalSorting.direction === 'desc') {
        newDirection = null;
      }
    }

    const newSorting = { field, direction: newDirection };
    setInternalSorting(newSorting);
    sorting?.onSort?.(field, newDirection);
  };

  // Sort data if no external sorting
  const sortedData = useMemo(() => {
    if (sorting?.onSort || !internalSorting.direction) return data;

    return [...data].sort((a, b) => {
      const aValue = a[internalSorting.field];
      const bValue = b[internalSorting.field];

      if (aValue === bValue) return 0;

      const comparison = aValue < bValue ? -1 : 1;
      return internalSorting.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, internalSorting, sorting?.onSort]);

  // Handle selection
  const handleSelectAll = (checked: boolean) => {
    if (!selection?.onSelectionChange || !selection?.getRowKey) return;

    if (checked) {
      const allKeys = sortedData.map(selection.getRowKey);
      selection.onSelectionChange(allKeys);
    } else {
      selection.onSelectionChange([]);
    }
  };

  const handleSelectRow = (record: T, checked: boolean) => {
    if (!selection?.onSelectionChange || !selection?.getRowKey) return;

    const key = selection.getRowKey(record);
    const newSelectedKeys = checked
      ? [...selection.selectedKeys, key]
      : selection.selectedKeys.filter((k) => k !== key);

    selection.onSelectionChange(newSelectedKeys);
  };

  // Render cell content
  const renderCell = (column: Column<T>, record: T, index: number) => {
    const value = record[column.key];

    if (column.render) {
      return column.render(value, record, index);
    }

    // Auto-render status badges
    if (
      column.key.toLowerCase().includes('status') &&
      typeof value === 'string'
    ) {
      return <FeedbackStatus status={value} />;
    }

    return value;
  };

  // Header is shared between the populated table and the (optional)
  // header-preserving empty state.
  const renderHeader = () => (
    <thead className="bg-gray-50">
      <tr>
        {/* Selection column */}
        {selection && (
          <th className={`${cellPadding[size]} text-left`}>
            <input
              type="checkbox"
              checked={
                selection.selectedKeys.length === sortedData.length &&
                sortedData.length > 0
              }
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
          </th>
        )}

        {/* Data columns */}
        {columns.map((column) => (
          <th
            key={column.key}
            className={`${
              cellPadding[size]
            } text-left text-[14px] font-medium text-gray-500 tracking-wider whitespace-nowrap ${
              column.key === 'actions'
                ? 'sticky right-0 z-[1] bg-gray-50 shadow-left'
                : column.sticky === 'right'
                  ? 'sticky right-0 bg-gray-50 border-l border-gray-200 z-10'
                  : column.sticky === 'left'
                    ? 'sticky left-0 bg-gray-50 border-r border-gray-200 z-10'
                    : ''
            }`}
            style={{ width: column.width }}
          >
            <div
              className={`flex items-center gap-1 ${
                column.align === 'center'
                  ? 'justify-center'
                  : column.align === 'right'
                    ? 'justify-end'
                    : ''
              }`}
            >
              <span>{column.title}</span>
              {column.titleBadge && (
                <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                  {column.titleBadge}
                </span>
              )}
              {column.sortable && (
                <button
                  onClick={() => handleSort(column.key)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i
                    className={`text-[12px] ${
                      internalSorting.field === column.key
                        ? internalSorting.direction === 'asc'
                          ? 'ri-arrow-up-line'
                          : internalSorting.direction === 'desc'
                            ? 'ri-arrow-down-line'
                            : 'ri-expand-up-down-line'
                        : 'ri-expand-up-down-line'
                    }`}
                  ></i>
                </button>
              )}
            </div>
          </th>
        ))}

        {/* Actions column */}
        {rowActions && (
          <th
            className={`${cellPadding[size]} sticky right-0 bg-gray-50 border-l border-gray-200 z-10`}
          >
            <span className="sr-only">Actions</span>
          </th>
        )}
      </tr>
    </thead>
  );

  // One shell for every "no rows to show" state (empty, error): phone list,
  // header-preserving table, or plain card — differing only in the content and
  // its padding.
  const renderPlaceholder = (
    content: React.ReactNode,
    mobilePadClass: string,
    desktopPadClass: string
  ) => {
    // Phone rows have no column header to preserve.
    if (useMobileRows) {
      return (
        <div className={`bg-white ${className}`.trim()}>
          <div className={mobilePadClass}>{content}</div>
        </div>
      );
    }

    // Keep the column headers above the placeholder when asked, so the table
    // still reads as a table before any row exists.
    if (showHeaderWhenEmpty) {
      return (
        <div
          className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`.trim()}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              {renderHeader()}
              <tbody className="bg-white">
                <tr>
                  <td colSpan={totalColumns} className={desktopPadClass}>
                    {content}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div
        className={`bg-white rounded-lg border border-gray-200 ${className}`.trim()}
      >
        <div className={desktopPadClass}>{content}</div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    if (useMobileRows) {
      return (
        <div className={`bg-white ${className}`.trim()}>
          <SkeletonList rows={6} />
        </div>
      );
    }

    const skeletonColumns =
      columns.length + (selection ? 1 : 0) + (rowActions ? 1 : 0) || 1;

    return (
      <div
        className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`.trim()}
        aria-busy="true"
        data-testid="table-skeleton"
      >
        {/* Header bar, then one placeholder cell per column per row. */}
        <div className="flex h-12 items-center gap-4 border-b border-gray-200 bg-gray-50 px-6">
          {Array.from({ length: skeletonColumns }, (_, i) => (
            <Skeleton key={i} className="h-3 flex-1" />
          ))}
        </div>
        {Array.from({ length: 5 }, (_, rowIndex) => (
          <div
            key={rowIndex}
            className="flex h-16 items-center gap-4 border-t border-gray-200 px-6"
          >
            {Array.from({ length: skeletonColumns }, (_, i) => (
              <Skeleton key={i} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  // Error state. Loading wins over it; it wins over the empty state, because
  // "no rows" after a failed request is not an empty list.
  if (error) {
    const errorCard = (
      <FeedbackError
        description={typeof error === 'string' ? error : undefined}
        onRetry={onRetry}
      />
    );

    return renderPlaceholder(errorCard, 'px-4 py-6', 'p-6');
  }

  // Empty state
  if (!data.length) {
    // Auto-detect search scenario by checking URL params or document title
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam =
      urlParams.get('search') || urlParams.get('query') || urlParams.get('q');

    // Also check for search input in the document
    const searchInput = document.querySelector(
      'input[placeholder*="Search"], input[placeholder*="search"], input[type="search"]'
    ) as HTMLInputElement;
    const searchTerm = searchParam || searchInput?.value || '';

    // Filtered-empty offers a way out of the filter; genuinely-empty offers
    // the create CTA. Never render the state with no action when one exists.
    const resetAction = onResetFilters ? (
      <Button variant="white" onClick={onResetFilters}>
        Reset filter
      </Button>
    ) : (
      emptyAction
    );

    const emptyContent = emptyState || (
      <FeedbackEmpty
        title={
          searchTerm
            ? `Tidak ada hasil untuk "${searchTerm}"`
            : emptyTitle || 'Tidak ada data'
        }
        description={
          searchTerm
            ? 'Periksa ejaan atau coba kata kunci lain.'
            : emptyDescription
        }
        action={searchTerm ? resetAction : emptyAction}
      />
    );

    return renderPlaceholder(
      emptyContent,
      'px-4 py-12 text-center',
      'p-12 text-center'
    );
  }

  // Phone: flat row list, edge-to-edge tap targets (Template 1).
  if (useMobileRows && mobileRow) {
    const rowClasses =
      'w-full text-left min-h-[64px] px-4 py-2 flex items-center gap-3 active:bg-gray-100 focus-visible:outline-none';

    return (
      // Explicit role on purpose: Tailwind preflight sets `list-style: none`,
      // which makes Safari/VoiceOver drop the implicit list semantics.
      // eslint-disable-next-line jsx-a11y/no-redundant-roles
      <ul
        role="list"
        data-tap-region
        className={`divide-y divide-gray-200 bg-white ${className}`.trim()}
      >
        {sortedData.map((record, index) => {
          const rowKey = selection?.getRowKey?.(record) || index.toString();
          const fullWidth = fullWidthRow?.(record, index);
          if (fullWidth != null) return <li key={rowKey}>{fullWidth}</li>;
          const body = (
            <>
              <div className="flex-1 min-w-0">{mobileRow(record, index)}</div>
              {onRowClick && (
                <i
                  className="ri-arrow-right-s-line text-xl text-gray-400 select-none"
                  aria-hidden="true"
                />
              )}
            </>
          );

          return (
            <li key={rowKey}>
              {onRowClick ? (
                <button
                  type="button"
                  className={rowClasses}
                  onClick={() => onRowClick(record, index)}
                >
                  {body}
                </button>
              ) : (
                <div className={rowClasses}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          {/* Header */}
          {renderHeader()}

          {/* Body */}
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.map((record, index) => {
              const rowKey = selection?.getRowKey?.(record) || index.toString();
              const isSelected =
                selection?.selectedKeys.includes(rowKey) || false;
              const fullWidth = fullWidthRow?.(record, index);
              if (fullWidth != null) {
                return (
                  <tr key={rowKey}>
                    <td colSpan={totalColumns} className="p-0">
                      {fullWidth}
                    </td>
                  </tr>
                );
              }

              return (
                <tr
                  key={rowKey}
                  className={`hover:bg-gray-50 ${
                    isSelected ? 'bg-purple-50' : ''
                  } ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={(e) => {
                    if (!onRowClick) return;
                    // Don't trigger row navigation when the click originated
                    // from an interactive control inside the row (action menus,
                    // links, checkboxes, inputs, or anything explicitly opted
                    // out via data-no-row-click).
                    const target = e.target as HTMLElement;
                    if (
                      target.closest(
                        'button, a, input, select, textarea, label, [role="menu"], [role="menuitem"], [data-no-row-click]'
                      )
                    ) {
                      return;
                    }
                    onRowClick(record, index);
                  }}
                >
                  {/* Selection column */}
                  {selection && (
                    <td className={cellPadding[size]}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) =>
                          handleSelectRow(record, e.target.checked)
                        }
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                    </td>
                  )}

                  {/* Data columns */}
                  {columns.map((column, columnIndex) => (
                    <td
                      key={column.key}
                      className={`${cellPadding[size]} ${
                        sizeClasses[size]
                      } text-gray-900 whitespace-nowrap ${
                        column.align === 'center'
                          ? 'text-center'
                          : column.align === 'right'
                            ? 'text-right'
                            : ''
                      } ${
                        column.key === 'actions'
                          ? 'sticky right-0 z-[2] bg-white shadow-left'
                          : ''
                      }`}
                    >
                      {renderCell(column, record, index)}
                    </td>
                  ))}

                  {/* Actions column */}
                  {rowActions && (
                    <td
                      className={`${cellPadding[size]} sticky right-0 bg-white border-l border-gray-200 z-10 text-right ${sizeClasses[size]} font-medium`}
                    >
                      {rowActions.render(record, index)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableData;
